import Api from '@/modules/core/api/api.ts';
import { SystemEndPoint } from '@/modules/core/enums/system-end-point.enum.ts';
import { TableId } from '@/modules/common/enums/table-id.enum.ts';
import { ErrorUtil } from '@/modules/common/utils/error.util.ts';
import { notification } from '@/modules/common/composables/use-notification.ts';
import { DateTime } from 'luxon';
import { ServerTimeAdapter } from '@/modules/common/adapters/server-time.adapter.ts';
import { AccessAdapter } from '@/modules/core/adapters/access.adapter.ts';
import { getCommentMarkdownRequest } from '@/modules/common/utils/comment-markdown-request.util.ts';
import { CommentType } from '@/modules/common/enums/comment-type.enum.ts';

const COMMENT_IDS_LIMIT = 100;

export type CommentFormData = {
  commentId: number;
  comment: string;
};

export type AddCommentParameters = {
  tableId: TableId;
  parentId: number;
  comment: string;
  commentMarkdown?: string;
};

export type EditCommentParameters = {
  tableId: TableId;
  parentId: number;
  formData: CommentFormData;
  commentMarkdown?: string;
};

export type DeleteCommentParameters = {
  commentId: number;
};

export type GetDeadlineParameters = {
  days: number;
  from?: DateTime;
};

export type GetWorkDaysDiffParameters = {
  from: DateTime;
  to: DateTime;
};

export class SystemApi {
  static async unread(id: number, tableId: TableId) {
    try {
      await Api.post(SystemEndPoint.Unread, {
        parent_table_id: tableId,
        parent_id: id,
      });
    } catch (error) {
      // TODO: Выводить нотификацию автоматически в глобальном обработчике
      if (ErrorUtil.isUnexpected(error)) {
        notification.error('core.error.unread', error);
      }

      throw error;
    }
  }

  static async readAll(tableId: TableId) {
    try {
      await Api.post(SystemEndPoint.ReadAll, {
        table_id: tableId,
      });
    } catch (error) {
      // TODO: Выводить нотификацию автоматически в глобальном обработчике
      if (ErrorUtil.isUnexpected(error)) {
        notification.error('core.error.readAll', error);
      }

      throw error;
    }
  }

  static async getServerId(signal?: AbortSignal) {
    const response = await Api.post(SystemEndPoint.GetServerId, {}, signal, true, { key: 'SERVER_ID' });
    return response.server_id;
  }

  static async getUserId(signal?: AbortSignal) {
    const response = await Api.post(SystemEndPoint.GetUserId, {}, signal, true, { key: 'SYSTEM_USER_ID' });
    return response.login_id as number;
  }

  static async getAutoSignEnabled() {
    const response = await Api.post(SystemEndPoint.GetAutoSignStatus);
    return !!response.status;
  }

  static async addComment(parameters: AddCommentParameters, signal?: AbortSignal) {
    await Api.post(
      SystemEndPoint.AddComment,
      {
        parent_table_id: parameters.tableId,
        parent_id: parameters.parentId,
        comment: parameters.comment,
        ...getCommentMarkdownRequest(parameters.commentMarkdown),
      },
      signal
    );
  }

  static async editComment(parameters: EditCommentParameters, signal?: AbortSignal) {
    await Api.post(
      SystemEndPoint.EditComment,
      {
        comment_id: parameters.formData.commentId,
        comment: parameters.formData.comment,
        ...getCommentMarkdownRequest(parameters.commentMarkdown),
      },
      signal
    );
  }

  static async deleteComment(parameters: DeleteCommentParameters, signal?: AbortSignal) {
    await Api.post(SystemEndPoint.DeleteComment, { comment_id: parameters.commentId }, signal);
  }

  static async getCommentsMarkdown(commentIds: number[], signal?: AbortSignal) {
    const markdown = new Map<number, string>();

    if (!commentIds.length) {
      return markdown;
    }

    const chunks: number[][] = [];
    for (let index = 0; index < commentIds.length; index += COMMENT_IDS_LIMIT) {
      chunks.push(commentIds.slice(index, index + COMMENT_IDS_LIMIT));
    }

    const responses = await Promise.all(
      chunks.map((chunk) => Api.post(SystemEndPoint.GetCommentsMarkdown, { comment_ids: chunk }, signal))
    );

    for (const response of responses) {
      for (const item of (response?.items ?? []) as Record<string, unknown>[]) {
        if (Number(item['TYPE_ID']) !== CommentType.Markdown) {
          continue;
        }

        const id = item['PARENT_ID'];
        const text = item['Текст'];

        if (typeof id !== 'number' || typeof text !== 'string') {
          continue;
        }

        const value = text.trim();

        if (value) {
          markdown.set(id, value);
        }
      }
    }

    return markdown;
  }

  static async hasReplicationSystem(signal: AbortSignal) {
    const response = await Api.post(SystemEndPoint.CheckReplicationSystem, {}, signal);
    return response.check_rpl_objects !== 0;
  }

  static async getDeadline({ days, from = DateTime.now() }: GetDeadlineParameters, signal?: AbortSignal) {
    const response = await Api.post(
      SystemEndPoint.GetDeadline,
      {
        count_day: days,
        date_start: from.toSQLDate(),
      },
      signal
    );

    return DateTime.fromFormat(response.date as string, 'dd.MM.yyyy');
  }

  static async getWorkDaysDiff({ from, to }: GetWorkDaysDiffParameters, signal?: AbortSignal) {
    const response = await Api.post(
      SystemEndPoint.GetWorkDaysDiff,
      {
        date_start: from,
        date_end: to,
      },
      signal
    );

    return response.count_days as number;
  }

  static async getServerTime(signal: AbortSignal) {
    const response = await Api.post(SystemEndPoint.GetTimezone, {}, signal);
    return ServerTimeAdapter.map(response);
  }

  static async getAccessList() {
    const response = await Api.post(SystemEndPoint.GetGuiElements);
    return AccessAdapter.mapList(response);
  }
}
